import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Card, Container, Flex, Heading, Link as RadixLink, Text,
} from '@radix-ui/themes';
import config from '../../config';
import type { Event, Venue } from '../../types';

interface EventWithVenue extends Event {
  venue: Venue;
}

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<EventWithVenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/events/${id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.status === 404) {
          setNotFound(true);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setEvent(data);
      } catch (err) {
        console.error('Error fetching event:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEvent();
    }
  }, [id]);

  const formatDateTime = (event: EventWithVenue) => {
    if (event.type === 'recurring' && !event.date) {
      // For recurring events without a fixed date, show the recurring schedule info
      const time = event.recurringTime
        ? formatRecurringTime(event.recurringTime)
        : null;
      return {
        day: event.recurringDay
          ? event.recurringDay.charAt(0).toUpperCase() + event.recurringDay.slice(1) + 's'
          : 'TBD',
        date: 'Weekly',
        time: time ?? 'TBD',
      };
    }

    const date = new Date(event.date ?? new Date());

    if (isNaN(date.getTime())) {
      return { day: 'TBD', date: 'TBD', time: 'TBD' };
    }

    return {
      day: date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/Denver' }),
      date: date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'America/Denver',
      }),
    };
  };

  const formatRecurringTime = (timeStr: string): string => {
    const [h, m] = timeStr.split(':').map(Number);
    const d = new Date();
    d.setHours(h ?? 19, m ?? 30, 0, 0);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: 'America/Denver',
    });
  };

  if (loading) {
    return (
      <Box p="6" style={{ minHeight: '60vh' }}>
        <Text as="p" align="center" color="gray" size="4" mt="9">Loading event...</Text>
      </Box>
    );
  }

  if (notFound || !event) {
    return (
      <Box p="6" style={{ minHeight: '60vh' }}>
        <Flex direction="column" align="center" gap="3" mt="9">
          <Heading size="6">Event Not Found</Heading>
          <Text color="gray">The event you are looking for does not exist or has been removed.</Text>
          <RadixLink asChild>
            <Link to="/">Back to schedule</Link>
          </RadixLink>
        </Flex>
      </Box>
    );
  }

  const { day, date, time } = formatDateTime(event);

  return (
    <Box p={{ initial: '5', md: '6' }} style={{ minHeight: '60vh' }}>
      <Container size="3">
        <Box mb="4">
          <RadixLink asChild weight="medium">
            <Link to="/">&#8592; Back to schedule</Link>
          </RadixLink>
        </Box>

        <Card size="4">
          {event.imageUrl && (
            <img
              src={event.imageUrl}
              alt={event.name}
              style={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'cover',
                borderRadius: 'var(--radius-3)',
                marginBottom: 'var(--space-5)',
              }}
            />
          )}

          <Heading as="h1" size={{ initial: '6', md: '7' }} mb="4">
            {event.name}
          </Heading>

          {event.venue && (
            <Flex
              align="center"
              gap="4"
              mb="4"
              p="3"
              style={{ background: 'var(--gray-2)', borderRadius: 'var(--radius-3)' }}
            >
              {event.venue.logo && (
                <img
                  src={event.venue.logo}
                  alt={`${event.venue.name} logo`}
                  style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain' }}
                />
              )}
              <Text size="4" weight="medium">
                {event.venue.link ? (
                  <RadixLink href={event.venue.link} target="_blank" rel="noreferrer">
                    {event.venue.name}
                  </RadixLink>
                ) : (
                  event.venue.name
                )}
              </Text>
            </Flex>
          )}

          <Flex
            direction="column"
            gap="1"
            p="3"
            style={{
              background: 'var(--gray-2)',
              border: '1px solid var(--gray-5)',
              borderRadius: 'var(--radius-3)',
            }}
          >
            <Text size="4" weight="medium">{day}</Text>
            <Text color="gray">{date}</Text>
            <Text color="gray">{time}</Text>
          </Flex>

          {event.venue?.address && (
            <Box mt="5" style={{ borderRadius: 'var(--radius-3)', overflow: 'hidden' }}>
              <iframe
                title="Venue location"
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(event.venue.address)}`}
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '12px', display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          )}
        </Card>
      </Container>
    </Box>
  );
};

EventDetail.displayName = 'EventDetail';

export default EventDetail;
